import { useEffect, useRef } from "preact/hooks";

export function clientDataIsQPay(data: ClientSideData): data is QPayClientData {
  return !!(data as QPayClientData).ExtraFields_f14;
}

export function clientDataIsAdyen(
  data: ClientSideData
): data is AdyenClientData {
  return !!(data as AdyenClientData).adyen_client_key;
}

export function clientDataIsPaytabs(
  data: ClientSideData
): data is PayTabsClientData {
  return !!(data as PayTabsClientData).paytabs_client_key;
}

export function clientDataIsYoco(data: ClientSideData): data is YocoClientData {
  return !!(data as YocoClientData).yoco_public_key;
}

export function clientDataIsInterSwitch(
  data: ClientSideData
): data is InterSwitchClientData {
  return !!(data as InterSwitchClientData).trans_ref;
}

export function clientDataIsMpgs(data: ClientSideData): data is MpgsClientData {
  return !!(data as MpgsClientData).mpgs_acquirer_url;
}

export function clientDataIsGeidea(
  data: ClientSideData
): data is GeideaClientData {
  return !!(data as GeideaClientData).geidea_session_id;
}

export function clientDataIsNetworkInternational(
  data: ClientSideData
): data is NetworkInternationalClientData {
  return !!(data as NetworkInternationalClientData).outlet_reference;
}

export function clientDataIsNetworkInternationalSamsunPay(
  data: ClientSideData
): data is NetworkInternationalSamsungPayClientData {
  return !!(data as NetworkInternationalSamsungPayClientData).callbackUrl;
}

export function clientDataIsDibsy(
  data: ClientSideData
): data is DibsyClientData {
  return !!(data as DibsyClientData).dibsy_public_key;
}
export function clientDataIsFoloosi(
  data: ClientSideData
): data is FoloosiClientData {
  return !!(data as FoloosiClientData).foloosi_merchant_key;
}

export function clientDataIsMyFatoorah(
  data: ClientSideData
): data is MyFatoorahClientData {
  return !!(data as MyFatoorahClientData).base_form_url;
}

export function clientDataIsCareem(
  data: ClientSideData
): data is CareemClientData {
  return !!(data as CareemClientData).careem_invoice_id;
}

export function clientDataIsTabby(
  data: ClientSideData
): data is TabbyClientData {
  return (
    !!(data as TabbyClientData).submit_url &&
    !!(data as TabbyClientData).tabby_icon
  );
}

export function clientDataIsFawryValue(
  data: ClientSideData
): data is FawryValueClientData {
  return (
    !!(data as FawryValueClientData).TENURE_LIST &&
    !!(data as FawryValueClientData).trx_uuid
  );
}

export function clientDataIsPaypal(
  data: ClientSideData
): data is PaypalClientData {
  return !!(data as PaypalClientData).paypal_client_id;
}

export function clientDataIsStripe(
  data: ClientSideData
): data is StripeClientData {
  return !!(data as StripeClientData).stripe_client_secret;
}

export function clientDataIsTap(data: ClientSideData): data is TapClientData {
  return !!(data as TapClientData).trx_uuid;
}

export function clientDataIsCheckoutDotCom(
  data: ClientSideData
): data is CheckoutDotComClientData {
  return !!(data as CheckoutDotComClientData).checkoutdotcom_public_key;
}

// The bank transfer (ACH Flow) client data is distinguished from the card client data
// by carrying a Checkout payment session secret.
export function clientDataIsCheckoutDotComBankTransfer(
  data: ClientSideData
): data is CheckoutDotComBankTransferClientData {
  return !!(data as CheckoutDotComBankTransferClientData)
    .payment_session_secret;
}
export const usePrevious = <T,>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export function intentDataIsPaymentIntent(
  data: PaymentIntent | PayoutIntent | CardIntent
): data is PaymentIntent {
  return !!(data as PaymentIntent).payment_methods;
}

export function intentDataIsPayoutIntent(
  data: PaymentIntent | PayoutIntent | CardIntent
): data is PayoutIntent {
  return (
    !!(data as PayoutIntent).payout_methods ||
    !!(data as PayoutIntent).max_payout_amount
  );
}

export function isCustomFormActionData(
  data:
    | CustomFormActionData
    | CardActionData
    | PaymentActionData
    | PayoutActionData
): data is CustomFormActionData {
  return Array.isArray((data as CustomFormActionData).form_fields);
}

export function isPaymobActionData(
  data:
    | PaymobActionData
    | CustomFormActionData
    | CardActionData
    | PaymentActionData
    | PayoutActionData
): data is PaymobActionData {
  return (
    (data as PaymobActionData).provider_api_name === "paymob_cash_collection"
  );
}

export function isCyberSourceActionData(
  data: unknown
): data is CyberSourceActionData {
  return (data as CyberSourceActionData).provider_name === "Cyber Source";
}

export function isFawryActionData(
  data:
    | FawryActionData
    | CustomFormActionData
    | CardActionData
    | PaymentActionData
    | PayoutActionData
): data is FawryActionData {
  return (data as FawryActionData).provider_name === "Fawry";
}

export function isPaymentActionData(
  data:
    | CustomFormActionData
    | CardActionData
    | PaymentActionData
    | PayoutActionData
): data is PaymentActionData {
  return !!(
    (data as PaymentActionData).form_fields?.billing_fields &&
    (data as PaymentActionData).form_fields?.card_fields
  );
}

export function isPayoutActionData(
  data:
    | CustomFormActionData
    | CardActionData
    | PaymentActionData
    | PayoutActionData
): data is PayoutActionData {
  return !!(
    (data as PayoutActionData).form_fields?.billing_fields &&
    (data as PayoutActionData).provider_id
  );
}

export function isCardActionData(
  data:
    | CustomFormActionData
    | CardActionData
    | PaymentActionData
    | PayoutActionData
): data is CardActionData {
  return !!(data as CardActionData)?.form_fields?.card_fields;
}

export function isTransactionData(
  data: PayoutTransaction | PaymentTransaction | CardActionData
): data is PayoutTransaction | PaymentTransaction {
  return !!(data as PayoutTransaction | PaymentTransaction).id;
}

export function isPayIntent(
  data: PayoutIntent | PaymentIntent | CardIntent
): data is PayoutIntent | PaymentIntent {
  return !!(data as PayoutIntent | PaymentIntent).amount;
}

export function isCardIntent(
  data: PayoutIntent | PaymentIntent | CardIntent
): data is CardIntent {
  return !!(data as CardIntent).standalone_tokenization;
}

export function isPaymentIntent(
  data: PayoutIntent | PaymentIntent | CardIntent
): data is PaymentIntent {
  return Object.prototype.hasOwnProperty.call(data, "hide_retry_button");
}
export function isDonationTemplate(
  template: IntentTemplate
): template is IntentDonationTemplate {
  return template?.template_id === "adjustable_custom_amounts";
}

export const safeLocalStorage = {
  isLocalStorageAvailable() {
    const test = "test";
    try {
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  },
  setItem(name: string, value: string) {
    if (this.isLocalStorageAvailable()) {
      localStorage.setItem(name, value);
    }
  },
  getItem(name: string) {
    if (this.isLocalStorageAvailable()) {
      return localStorage.getItem(name);
    }
    return "";
  },
  removeItem(name: string) {
    if (this.isLocalStorageAvailable()) {
      localStorage.removeItem(name);
    }
  },
};

export function redirectEntireWindow(redirectUrl: string) {
  const serverRedirectUrl = `${
    import.meta.env.VITE_API_BASE
  }/api/v1.1/payments/server-redirect/?redirect_url=${redirectUrl}`;

  try {
    window.top.location.href = serverRedirectUrl;
  } catch (e) {
    if ((e as DOMException).name === "SecurityError") {
      // if iframe can't change top frame url, open in a new window
      window.open(serverRedirectUrl, "_blank");
      return;
    }

    throw e;
  }
}
